<!--
  No backdrop overlay: the reported screen must remain visible and usable
  while the reporter describes it.
-->

<!--
  Collapsed state. The panel is still mounted behind this, holding the draft —
  only the corner button is on screen, so the reporter can photograph the page
  the ticket is about.
-->
@if (isOpen() && minimized() && !capturing()) {
  <button
    type="button"
    class="capture-restore"
    data-testid="ticket-capture-restore"
    (click)="restore()"
    [attr.aria-label]="'Support_TicketCapture:restore' | translate"
    [title]="'Support_TicketCapture:restore' | translate"
  >
    <i class="ti ti-lifebuoy"></i>
    <span>{{ 'Support_TicketCapture:restore' | translate }}</span>
  </button>
}

<div
  class="capture-panel"
  [class.open]="isOpen() && !panelHidden()"
  data-testid="ticket-capture-panel"
>
  <div class="capture-header">
    <h4 class="capture-title">
      <i class="ti ti-lifebuoy"></i>
      {{ 'Support_TicketCapture:title' | translate }}
    </h4>
    <div class="capture-header-actions">
      <button
        type="button"
        class="capture-icon-button"
        data-testid="ticket-capture-minimize"
        [disabled]="submitting()"
        (click)="minimize()"
        [attr.aria-label]="'Support_TicketCapture:minimize' | translate"
        [title]="'Support_TicketCapture:minimize' | translate"
      >
        <i class="ti ti-minus"></i>
      </button>
      <button
        type="button"
        class="capture-icon-button capture-close"
        [disabled]="submitting()"
        (click)="closed.emit()"
        [attr.aria-label]="'Support_TicketCapture:close' | translate"
        [title]="'Support_TicketCapture:close' | translate"
      >
        <i class="ti ti-x"></i>
      </button>
    </div>
  </div>

  <div class="capture-body">
    <label class="capture-field">
      <span class="capture-label">{{ 'Support_TicketCapture:ticketTitle' | translate }}</span>
      <input
        type="text"
        class="capture-input"
        maxlength="255"
        data-testid="ticket-capture-title"
        [ngModel]="title()"
        (ngModelChange)="title.set($event)"
        [placeholder]="'Support_TicketCapture:titlePlaceholder' | translate"
      />
    </label>

    <div class="capture-field">
      <span class="capture-label">{{ 'Support_TicketCapture:category' | translate }}</span>
      <div class="capture-categories">
        @for (option of categories(); track option.id) {
          <button
            type="button"
            class="capture-category"
            [class.selected]="categoryId() === option.id"
            (click)="categoryId.set(option.id)"
          >
            {{ option.name }}
          </button>
        }
      </div>
    </div>

    <label class="capture-field">
      <span class="capture-label">{{ 'Support_TicketCapture:description' | translate }}</span>
      <textarea
        class="capture-textarea"
        rows="6"
        data-testid="ticket-capture-description"
        [ngModel]="description()"
        (ngModelChange)="description.set($event)"
        [placeholder]="'Support_TicketCapture:descriptionPlaceholder' | translate"
      ></textarea>
      <small class="capture-hint">
        <i class="ti ti-shield-lock"></i>
        {{ 'Support_TicketCapture:phiHint' | translate }}
      </small>
    </label>

    <div class="capture-field">
      <span class="capture-label">{{ 'Support_TicketCapture:screenshot' | translate }}</span>
      @if (pendingFile(); as file) {
        <div class="capture-file">
          <i class="ti ti-photo"></i>
          <span class="capture-file-name">{{ file.name }}</span>
          <button
            type="button"
            class="capture-file-remove"
            [disabled]="submitting()"
            (click)="clearFile()"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>
      } @else {
        <div class="capture-attach-actions">
          @if (captureSupported) {
            <button
              type="button"
              class="capture-attach-button"
              data-testid="ticket-capture-screenshot"
              [disabled]="submitting() || capturing()"
              (click)="onTakeScreenshot()"
            >
              <i class="ti ti-camera"></i>
              {{
                (capturing()
                  ? 'Support_TicketCapture:capturing'
                  : 'Support_TicketCapture:takeScreenshot'
                ) | translate
              }}
            </button>
          }
          <label class="capture-upload">
            <input
              type="file"
              accept="image/png"
              [disabled]="submitting() || capturing()"
              (change)="onFileSelected($event)"
            />
            <span>
              <i class="ti ti-paperclip"></i>
              {{ 'Support_TicketCapture:attachScreenshot' | translate }}
            </span>
          </label>
        </div>
      }
      <small class="capture-hint">
        {{ 'Support_TicketCapture:screenshotHint' | translate }}
      </small>
      @if (attachmentError(); as error) {
        <small class="capture-error">
          {{ 'Support_TicketCapture:' + error | translate }}
        </small>
      }
    </div>

    <!-- Shown, not hidden: the reporter should know what is being attached. -->
    <div class="capture-context">
      <div class="capture-context-header">
        <i class="ti ti-info-circle"></i>
        {{ 'Support_TicketCapture:contextTitle' | translate }}
      </div>
      <ul class="capture-context-list">
        @if (contextPreview().routePath) {
          <li>
            <span>{{ 'Support_TicketCapture:contextScreen' | translate }}</span>
            <code>{{ contextPreview().routePath }}</code>
          </li>
        }
        <li>
          <span>{{ 'Support_TicketCapture:contextFailedRequests' | translate }}</span>
          <code>{{ contextPreview().failedRequestCount }}</code>
        </li>
        @if (contextPreview().appVersion) {
          <li>
            <span>{{ 'Support_TicketCapture:contextVersion' | translate }}</span>
            <code>{{ contextPreview().appVersion }}</code>
          </li>
        }
      </ul>
    </div>
  </div>

  <div class="capture-footer">
    <button type="button" class="capture-secondary" (click)="onViewMyTickets()">
      {{ 'Support_TicketCapture:myTickets' | translate }}
      <i class="ti ti-arrow-right"></i>
    </button>
    <button
      type="button"
      class="capture-submit"
      data-testid="ticket-capture-submit"
      [disabled]="!canSubmit()"
      (click)="submit()"
    >
      @if (submitting()) {
        <i class="ti ti-loader-2 spinning"></i>
      }
      {{ 'Support_TicketCapture:submit' | translate }}
    </button>
  </div>
</div>

<!--
  Outside the panel on purpose: the panel is hidden during a capture, and the
  redactor opens immediately afterwards. Nested, it would inherit that.

  `@defer` keeps the redactor — and through it Fabric — out of the initial
  bundle. This widget is mounted in the layout on every page, so anything it
  loads eagerly is paid for by every session, including the overwhelming
  majority that never file a ticket.
-->
@defer (when redactorSource()) {
  @if (redactorSource(); as source) {
    <o3-support-screenshot-redactor
      [source]="source"
      [fileName]="redactorFileName()"
      (applied)="onRedacted($event)"
      (cancelled)="onRedactorCancelled()"
    />
  }
}
